// Settings panel: vault settings, device tokens, password change.
import { formatTime, h } from './dom.js';
import { confirmDialog, openModal, promptDialog, toast } from './modals.js';
import type { NewToken, Settings, TokenInfo } from './types.js';
import type { App } from './app.js';

function copyable(value: string, label: string): HTMLElement {
  return h('div', { class: 'copy-row' }, [
    h('code', { class: 'copy-field', text: value }),
    h('button', {
      class: 'ghost-button',
      text: 'Copy',
      type: 'button',
      on: {
        click: () => {
          void navigator.clipboard.writeText(value).then(
            () => toast(`${label} copied`, { kind: 'success' }),
            () => toast('Clipboard unavailable', { kind: 'error' }),
          );
        },
      },
    }),
  ]);
}

/** Modal panel for `/api/settings`, `/api/tokens` and `/api/auth/password`. */
export class SettingsPanel {
  private readonly app: App;

  constructor(app: App) {
    this.app = app;
  }

  open(): void {
    const modal = openModal({ title: 'Settings', wide: true, kind: 'settings' });
    const body = h('div', { class: 'settings-body' });
    modal.body.append(body);
    void this.render(body, modal);
  }

  private async render(body: HTMLElement, modal: { close(): void }): Promise<void> {
    body.replaceChildren(h('p', { class: 'empty-note', text: 'Loading settings…' }));
    const refresh = (): void => void this.render(body, modal);
    let settings: Settings;
    let tokens: TokenInfo[];
    try {
      const [settingsResponse, tokenResponse] = await Promise.all([this.app.api.settings(), this.app.api.tokens()]);
      settings = settingsResponse;
      tokens = tokenResponse.tokens;
    } catch (error) {
      body.replaceChildren(h('p', { class: 'error-text', text: `Settings failed: ${(error as Error).message}` }));
      return;
    }
    body.replaceChildren(
      this.renderVaultForm(settings, refresh),
      this.renderTokens(tokens, refresh),
      this.renderPassword(),
    );
  }

  private renderVaultForm(settings: Settings, refresh: () => void): HTMLElement {
    const vaultName = h('input', { class: 'text-input', type: 'text', value: settings.vaultName });
    const ignore = h('textarea', { class: 'text-input', rows: 8, value: settings.ignore.join('\n') });
    const maxBlob = h('input', { class: 'text-input', type: 'number', value: String(settings.maxBlobBytes) });
    const retention = h('input', { class: 'text-input', type: 'number', value: String(settings.historyRetentionDays) });
    const autoCommit = h('input', { type: 'checkbox' });
    autoCommit.checked = settings.autoCommit;
    const save = h('button', {
      class: 'primary-button',
      text: 'Save settings',
      type: 'button',
      on: {
        click: () => {
          void (async () => {
            try {
              await this.app.api.saveSettings({
                vaultName: vaultName.value.trim(),
                ignore: ignore.value
                  .split(/\r?\n/)
                  .map((line) => line.trim())
                  .filter((line) => line.length > 0),
                maxBlobBytes: Number(maxBlob.value) || settings.maxBlobBytes,
                historyRetentionDays: Number(retention.value) || 0,
                autoCommit: autoCommit.checked,
              });
              this.app.setVaultName(vaultName.value.trim());
              toast('Settings saved', { kind: 'success' });
              refresh();
            } catch (error) {
              toast(`Save failed: ${(error as Error).message}`, { kind: 'error' });
            }
          })();
        },
      },
    });
    return h('section', { class: 'panel-section' }, [
      h('h3', { text: 'Vault' }),
      h('div', { class: 'form-grid' }, [
        h('label', { class: 'field' }, [h('span', { text: 'Vault name' }), vaultName]),
        h('label', { class: 'field wide' }, [h('span', { text: 'Ignore patterns (one glob per line)' }), ignore]),
        h('label', { class: 'field' }, [h('span', { text: 'Max blob bytes' }), maxBlob]),
        h('label', { class: 'field' }, [h('span', { text: 'History retention (days)' }), retention]),
        h('label', { class: 'check-field' }, [autoCommit, h('span', { text: 'Auto-commit to git on change' })]),
      ]),
      h('div', { class: 'button-row' }, [save]),
    ]);
  }

  private renderTokens(tokens: TokenInfo[], refresh: () => void): HTMLElement {
    const list = h('div', { class: 'token-list' });
    if (tokens.length === 0) list.append(h('p', { class: 'empty-note', text: 'No device tokens yet.' }));
    for (const token of tokens) {
      list.append(
        h('div', { class: `token-row${token.revoked ? ' revoked' : ''}` }, [
          h('div', { class: 'token-info' }, [
            h('span', { class: 'token-label', text: token.label }),
            h('span', { class: 'token-meta', text: `created ${formatTime(token.createdAt)} · last used ${formatTime(token.lastUsedAt)}${token.revoked ? ' · revoked' : ''}` }),
          ]),
          token.revoked
            ? null
            : h('button', {
                class: 'ghost-button small',
                text: 'Revoke',
                type: 'button',
                on: {
                  click: () => {
                    void (async () => {
                      const ok = await confirmDialog('Revoke token', `Revoke “${token.label}”? Devices using it stop syncing.`, 'Revoke');
                      if (!ok) return;
                      try {
                        await this.app.api.revokeToken(token.id);
                        toast('Token revoked', { kind: 'success' });
                        refresh();
                      } catch (error) {
                        toast(`Revoke failed: ${(error as Error).message}`, { kind: 'error' });
                      }
                    })();
                  },
                },
              }),
        ]),
      );
    }
    const create = h('button', {
      class: 'ghost-button',
      text: 'New token…',
      type: 'button',
      on: {
        click: () => {
          void (async () => {
            const label = await promptDialog({
              title: 'New device token',
              label: 'Device label',
              value: 'my-device',
              confirmLabel: 'Create token',
            });
            if (label === null || label === '') return;
            try {
              const created: NewToken = await this.app.api.createToken(label);
              const modal = openModal({ title: 'Token created' });
              modal.body.append(
                h('p', { class: 'modal-text', text: 'Copy this token now — it is shown only once.' }),
                copyable(created.token, 'Token'),
                h('div', { class: 'modal-actions' }, [
                  h('button', { class: 'primary-button', text: 'Done', type: 'button', on: { click: () => modal.close() } }),
                ]),
              );
              refresh();
            } catch (error) {
              toast(`Token creation failed: ${(error as Error).message}`, { kind: 'error' });
            }
          })();
        },
      },
    });
    return h('section', { class: 'panel-section' }, [h('h3', { text: 'Device tokens' }), list, h('div', { class: 'button-row' }, [create])]);
  }

  private renderPassword(): HTMLElement {
    const current = h('input', { class: 'text-input', type: 'password', value: '', placeholder: 'current password' });
    const next = h('input', { class: 'text-input', type: 'password', value: '', placeholder: 'new password' });
    const repeat = h('input', { class: 'text-input', type: 'password', value: '', placeholder: 'repeat new password' });
    const submit = h('button', {
      class: 'primary-button',
      text: 'Change password',
      type: 'button',
      on: {
        click: () => {
          void (async () => {
            if (next.value.length < 8) {
              toast('New password must be at least 8 characters', { kind: 'error' });
              return;
            }
            if (next.value !== repeat.value) {
              toast('Passwords do not match', { kind: 'error' });
              return;
            }
            try {
              await this.app.api.changePassword(current.value, next.value);
              current.value = '';
              next.value = '';
              repeat.value = '';
              toast('Password changed', { kind: 'success' });
            } catch (error) {
              toast(`Change failed: ${(error as Error).message}`, { kind: 'error' });
            }
          })();
        },
      },
    });
    return h('section', { class: 'panel-section' }, [
      h('h3', { text: 'Password' }),
      h('div', { class: 'form-grid' }, [
        h('label', { class: 'field' }, [h('span', { text: 'Current' }), current]),
        h('label', { class: 'field' }, [h('span', { text: 'New' }), next]),
        h('label', { class: 'field' }, [h('span', { text: 'Repeat' }), repeat]),
      ]),
      h('div', { class: 'button-row' }, [submit]),
    ]);
  }
}
